import {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Col, Row } from 'reactstrap';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { v7 as uuid } from 'uuid';
import { push } from 'redux-first-history';
import moment from 'moment';
import { isEmpty } from 'lodash-es';
import { useSelector, useDispatch } from '@s20/hooks/redux.hooks';
import { useWeekStartsOn } from '@s20/hooks/tenant.hooks';
import { getUser } from '@s20/selectors/features/common.selectors';
import { convertTimezone, getWeekStartDate, resolveJobTimeZone } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import { FlexEndRow, toasty } from '../../common/forms/FormElements';
import { ApiRoutes, AppNavPaths } from '../../Common';
import { SaveButton } from '../../common/forms/SaveButton';
import { Project } from '../../projectManager/Project';
import SlideForm from '../../common/forms/SlideForm';
import { getDuplicateJobs, showDuplicateJobsModal } from '../../job/common/DuplicateJobDetection';
import { BaseQuickJob } from './QuickJob';
import { Section } from './Section';
import { Contract } from './Contract';
import { DispatchOffice } from './DispatchOffice';
import { DispatchPort } from './DispatchPort';
import { Customer } from './Customer';
import { Job } from './Job';
import { Resources } from './Resources';
import './index.scss';

export const QuickJob = ({
    show, onClose, selectedJobRequest = null, isQuickRental = false,
}) => {
    const tempSessionIdRef = useRef(`temp-${uuid()}`);
    const user = useSelector(getUser);
    const weekStartsOn = useWeekStartsOn();

    const dispatch = useDispatch();

    const [isValidated, setIsValidated] = useState(false);
    const formRef = useRef(null);

    // The offices the "Which office is dispatching this job?" picker lists (same query key, so
    // one fetch); an office's zone is the fallback for a job location that has none.
    const { data: dispatchOffices } = useQuery({
        queryKey: ['dispatchOffices'],
        queryFn: () => httpClient.js(ApiRoutes.typeAheads.selectableDispatchLocations()),
    });

    useEffect(() => {
        if (show) {
            setIsValidated(false);
        }
    }, [show]);

    const getDispatchPath = useCallback((dispatchOfficeLocationId) => {
        let path = `${AppNavPaths.Dispatch}`;

        if (user.isAdmin
            || (
                user.expandedDispatchCompanies
                && user.expandedDispatchCompanies.length > 0
                && user.expandedDispatchCompanies.includes(dispatchOfficeLocationId)
            )
        ) {
            path += `?dispatchOfficeId=${dispatchOfficeLocationId}`;
        }

        return path;
    }, [user]);

    const saveJob = useCallback(async (job) => {
        let response = null;
        let createdJobId = null;

        if (job.projectId === -1) {
            const project = new Project();
            project.jobs = [job];
            project.meetingLocation = job.meetingLocation;
            project.jobLocation = job.jobLocation;
            project.requestedById = job.requestedById;
            project.workOrderNumber = job.workOrderNumber;
            project.subcontractorId = job.subcontractorId;
            project.jobTypeId = job.jobTypeId;
            project.contractId = job.contractId;
            project.dispatchCompanyId = job.dispatchOfficeLocationId;
            project.projectManagerId = job.projectManagerId || null;
            project.projectName = job.projectName;
            project.foremanId = job.foremenId;

            if (isQuickRental) {
                project.foremanId = job.requestedById;
                job.foremenId = job.requestedById;
            }

            job.dispatchPortId = job.dispatchPortId || null;

            response = await httpClient.post(
                ApiRoutes.project.createQuickProject(),
                project,
                httpClient.format.none,
            );

            if (response.ok) {
                let responseData = null;

                try {
                    responseData = await response.json();
                } catch (error) {
                    console.warn('Unable to parse quick project create response.', error);
                }

                createdJobId = responseData?.data ?? null;
            }
        } else {
            if (isQuickRental) {
                job.foremenId = job.requestedById;
            }

            response = await httpClient.post(
                ApiRoutes.job.create(),
                job,
                httpClient.format.none,
            );

            if (response.ok) {
                let responseData = null;

                try {
                    responseData = await response.json();
                } catch (error) {
                    console.warn('Unable to parse quick job create response.', error);
                }

                createdJobId = responseData?.data ?? null;
            }
        }

        if (response.ok) {
            toasty.success('Job Saved', 'Job saved successfully.');

            const path = getDispatchPath(job.dispatchOfficeLocationId);
            const [pathname, searchString] = path.split('?');
            const params = new URLSearchParams(searchString ?? '');

            if (createdJobId) {
                params.set('jobId', createdJobId);
            }

            if (job.startTime) {
                params.set('date', moment(job.startTime).format('YYYY-MM-DD'));
            }

            dispatch(push({
                pathname,
                search: params.toString() ? `?${params.toString()}` : '',
            }));
            onClose();
        }
    }, [dispatch, getDispatchPath, onClose, isQuickRental]);

    const handleSubmit = useCallback(async (values) => {
        const job = { ...values };

        if (job.startTime && job.invoicedDate) {
            const startDate = new Date(job.startTime);
            const invoiced = new Date(job.invoicedDate);

            if (startDate <= invoiced) {
                toasty.error('This date has already been invoiced. Please contact the billing team for changes.');
                return;
            }
        }
        // Revert uuid
        job.meetingLocation.id = typeof job.meetingLocation?.id === 'string'
            ? null
            : job.meetingLocation.id;

        job.jobLocation.id = typeof job.jobLocation?.id === 'string'
            ? null
            : job.jobLocation.id;

        job.resources = job.resources.map((r) => ({
            ...r,
            id: null,
            jobId: null,
            rentalDropoffDate: r.rentalDropoffQuantity > 0
                ? moment(job.startTime).format()
                : null,
            rentalPickupDate: r.rentalPickupQuantity > 0
                ? moment(job.startTime).format()
                : null,
        }));

        job.weekOfDate = getWeekStartDate(job.startTime, weekStartsOn);

        job.startTimeOffset = convertTimezone(job.startTime, resolveJobTimeZone(job, dispatchOffices));

        // check duplicates dispatches
        if (job.jobLocation) {
            const duplicateJobsList = await getDuplicateJobs({
                duplicateJobRequests: [{
                    jobDate: moment(job.startTime).toISOString(),
                    jobLocation: job.jobLocation,
                    excludeJobId: null,
                }],
            });
            if (duplicateJobsList.length > 0) {
                const proceed = await showDuplicateJobsModal(duplicateJobsList);
                if (!proceed) return;
            }
        }
        await saveJob(job);
    }, [saveJob, dispatchOffices, weekStartsOn]);

    // Prefetch data used in later steps
    useQuery({
        queryKey: ['usCounties', show],
        queryFn: () => show && httpClient.js(ApiRoutes.USCounties.all()),
    });

    // Prefetch data used in later steps
    useQuery({
        queryKey: ['jobTypes', show],
        queryFn: () => show && httpClient.js(ApiRoutes.typeAheads.jobTypes()),
    });

    const initialValues = useMemo(() => {
        const baseInitialValues = {
            ...new BaseQuickJob(),
            tempSessionId: tempSessionIdRef.current,
            isQuickRental,
        };

        if (!isEmpty(selectedJobRequest)) {
            return {
                ...baseInitialValues,
                jobRequestId: selectedJobRequest.id,
                companyId: selectedJobRequest.customerCompanyId,
                requestedById: selectedJobRequest.locationContactId,
                // Pre-fill the requested foreman (if any); dispatcher can still change it.
                foremenId: selectedJobRequest.foremanId,
                notes: `${selectedJobRequest.description}${selectedJobRequest.notes ? `\n\n${selectedJobRequest.notes}` : ''}`,
                purchaseOrderNumber: selectedJobRequest.purchaseOrderNumber,
                workOrderNumber: selectedJobRequest.workOrderNumber,
                startTime: moment(selectedJobRequest.requestedStartDate).format('YYYY-MM-DDT00:00'),

                // Just spread everything else to the address fields
                jobLocation: { ...selectedJobRequest },
                // The requestor may have supplied a distinct meeting location; fall back to
                // the job location for older requests captured before that was an option.
                meetingLocation: selectedJobRequest.meetingLocation
                    ? { ...selectedJobRequest.meetingLocation }
                    : { ...selectedJobRequest },
            };
        }

        return baseInitialValues;
    }, [selectedJobRequest, isQuickRental]);

    return createPortal(
        <SlideForm
            size="col-xl-12 col-md-12 col-xs-12"
            id="quickJobForm"
            name="quickJobForm"
            show={show}
            onClose={onClose}
            fullScreen
            hideToolbar
            minimize
            ref={formRef}
            formTitle={isQuickRental ? 'Create a New Quick Rental' : 'Create a New Job'}
            className="no-padding"
            onSubmit={handleSubmit}
            setIsValidated={(v) => setIsValidated(v)}
            isValidated={isValidated}
            initialValues={initialValues}
        >
            {({ values }) => (
                <div className="full-page-panel">
                    <Section>
                        <Row className="mt-3">
                            <Col
                                xs={12}
                                md={6}
                            >
                                <DispatchOffice />
                            </Col>

                            <Col
                                xs={12}
                                md={6}
                            >
                                <Customer disabled={Boolean(selectedJobRequest)} />

                            </Col>
                        </Row>

                        <DispatchPort dispatchOfficeId={values.dispatchOfficeLocationId} />

                        {Boolean(values.companyId) && Boolean(values.dispatchOfficeLocationId) && (
                            <Contract
                                companyId={values.companyId}
                                dispatchOfficeLocationId={values.dispatchOfficeLocationId}
                            />
                        )}
                    </Section>

                    {Boolean(values.contractId) && (
                        <>
                            <Section color="white">
                                <Job />
                            </Section>

                            {Boolean(values.startTime) && (
                                <Section>
                                    <Resources />

                                    <FlexEndRow>
                                        <SaveButton
                                            label="Create Job"
                                            size="lg"
                                            className="mt-4"
                                        />
                                    </FlexEndRow>
                                </Section>
                            )}
                        </>
                    )}
                </div>
            )}
        </SlideForm>,
        document.body,
    );
};
